import * as React from 'react';
import { useDispatch } from 'react-redux';
import SparklesIcon from '../../shared/SparklesIcon';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { DataCollectionAiAgentOperationType } from '../../../store/Models/Surveys/SurveyDataModel';
import {
    actionCreators as aiActionCreators
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import {
    SupplierResponseAlreadyProvidedResult,
    SurveyResponseSubmittedOfflineImportUnavailableMessage
} from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { useLocalizer } from './hooks/useLocalizer';
import { UploadedFile } from './types';
import { showSurveyResponseSubmittedOfflineImportUnavailablePopup } from '../../Survey/SurveyResponseSubmittedOfflineImportUnavailableModal';

interface AIProcessFilesButtonProps {
    requestId?: string;
    uploadedFiles: UploadedFile[];
    overrideDecision: boolean;
    autoAcceptSurveyResponse?: boolean;
    enableWebRetrieval?: boolean;
    webRetrievalUrls?: string[];
    userNotes?: string;
    disabled?: boolean;
    isProcessing?: boolean;
    onError?: (message: string) => void;
    localizer?: IStringLocalizer;
    operationType: DataCollectionAiAgentOperationType;
}

/**
 * Self-contained button component for triggering AI file processing
 * Handles the entire workflow including validation and API calls
 */
export const AIProcessFilesButton: React.FC<AIProcessFilesButtonProps> = ({
    requestId,
    uploadedFiles,
    overrideDecision,
    autoAcceptSurveyResponse = false,
    enableWebRetrieval = false,
    webRetrievalUrls = [],
    userNotes = '',
    disabled = false,
    isProcessing = false,
    onError,
    localizer,
    operationType
}) => {
    const dispatch = useDispatch();
    const localize = useLocalizer(localizer);

    /**
     * Helper function for consistent error handling
     */
    const handleError = (key: string) => {
        if (onError) {
            onError(localize(key));
        }
    };

    const showSubmittedResponseUnavailableMessage = (message?: string) => {
        if (showSurveyResponseSubmittedOfflineImportUnavailablePopup()) {
            return;
        }

        if (onError) {
            onError(localize(message || SurveyResponseSubmittedOfflineImportUnavailableMessage));
        }
    };

    /**
     * Processes uploaded files using the AI Survey Agent Controller
     */
    const processFiles = async () => {
        try {
            // Validate prerequisites
            if (!requestId) {
                handleError("Invalid request. Please refresh the page and try again.");
                return;
            }

            // Get valid file IDs
            const fileIds = uploadedFiles
                .filter(file => file.fileId)
                .map(file => file.fileId!);

            if (fileIds.length === 0) {
                handleError("Please upload at least one file for AI processing.");
                return;
            }

            // Trigger AI workflow via controller
            const result = await dispatch(
                aiActionCreators.triggerAIWorkflow(
                    requestId,
                    fileIds,
                    overrideDecision,
                    operationType,
                    autoAcceptSurveyResponse,
                    enableWebRetrieval,
                    webRetrievalUrls,
                    userNotes
                ) as any
            );

            if (!result.success) {
                if (result.result === SupplierResponseAlreadyProvidedResult) {
                    showSubmittedResponseUnavailableMessage(result.resultMessage);
                    return;
                }

                const errorKey = result.result === 'Error'
                    ? "AI processing failed. Please try again."
                    : "Failed to start AI processing. Please try again.";

                handleError(errorKey);
                return;
            }

        } catch (error) {
            handleError(
                "An unexpected error occurred during AI processing. Please try again."
            );
        }
    };

    const buttonText = localize('Process Files using AI');

    return (
        <button
            type="button"
            onClick={processFiles}
            className="ai-process-button"
            disabled={disabled || isProcessing || uploadedFiles.length === 0}
        >
            <SparklesIcon width={20} height={20} style={{ marginRight: '8px' }} />
            {buttonText}
        </button>
    );
};

export default AIProcessFilesButton;
